import React from 'react'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import type {
  SupportCenterHoursByWeekday,
  SupportCenterProfessionalRole,
  SupportCenterProfessionalRoleKind,
  SupportCenterProfileOption,
  SupportCenterSpecialization,
  SupportCenterSpecializationAssignment,
  SupportCenterUserAvailability,
} from '../../types/supportCenterSpecialties'
import { SUPPORT_CENTER_PROFESSIONAL_ROLE_LABELS } from '../../types/supportCenterSpecialties'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import {
  formatHourRanges,
  listHoursByWeekday,
  weekdaysShareHours,
} from '../../components/centralAmparo/scheduleHelpers'
import {
  MODULE_NAME,
  formatDate,
  getErrorMessage,
  type UserAssignmentRow,
} from '../../components/centralAmparo/parametrosHelpers'
import ParametrosAssignModal from './ParametrosAssignModal'
import ParametrosDeleteModal from './ParametrosDeleteModal'

function AvailabilitySchedule({
  hoursByWeekday,
  weekdays,
  hours,
}: {
  hoursByWeekday: SupportCenterHoursByWeekday
  weekdays: number[]
  hours: number[]
}) {
  const entries = listHoursByWeekday(hoursByWeekday, weekdays, hours)
  if (entries.length === 0) {
    return <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
  }

  if (entries.length > 1 && weekdaysShareHours(entries)) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap gap-1">
          {entries.map((entry) => (
            <span
              key={entry.day}
              className="inline-flex rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
            >
              {entry.label}
            </span>
          ))}
        </div>
        <span className="text-sm tabular-nums text-gray-600 dark:text-gray-300">
          {formatHourRanges(entries[0].hours)}
        </span>
      </div>
    )
  }

  const columns = entries.length > 3 ? 2 : 1
  const rows = Math.ceil(entries.length / columns)

  return (
    <ul
      className="grid w-max gap-x-5 gap-y-1"
      style={{ gridTemplateRows: `repeat(${rows}, auto)`, gridAutoFlow: 'column' }}
    >
      {entries.map((entry) => (
        <li key={entry.day} className="flex items-baseline gap-2 whitespace-nowrap">
          <span className="w-8 shrink-0 text-xs font-semibold text-gray-800 dark:text-gray-100">
            {entry.label}
          </span>
          <span className="text-sm tabular-nums text-gray-600 dark:text-gray-300">
            {formatHourRanges(entry.hours)}
          </span>
        </li>
      ))}
    </ul>
  )
}

const ParametrosUsuariosTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [specializations, setSpecializations] = React.useState<SupportCenterSpecialization[]>([])
  const [assignments, setAssignments] = React.useState<SupportCenterSpecializationAssignment[]>([])
  const [roles, setRoles] = React.useState<SupportCenterProfessionalRole[]>([])
  const [profiles, setProfiles] = React.useState<SupportCenterProfileOption[]>([])
  const [availabilityByUser, setAvailabilityByUser] = React.useState<
    Record<string, SupportCenterUserAvailability>
  >({})
  const [filter, setFilter] = React.useState('')

  const [assignModalOpen, setAssignModalOpen] = React.useState(false)
  const [assignEditingId, setAssignEditingId] = React.useState<string | null>(null)
  const [assignUserId, setAssignUserId] = React.useState('')
  const [assignRole, setAssignRole] =
    React.useState<SupportCenterProfessionalRoleKind>('atendente_triagem')
  const [assignSpecializationIds, setAssignSpecializationIds] = React.useState<string[]>([])
  const [assignWeekdays, setAssignWeekdays] = React.useState<number[]>([])
  const [assignHoursByWeekday, setAssignHoursByWeekday] =
    React.useState<SupportCenterHoursByWeekday>({})
  const [submittingAssign, setSubmittingAssign] = React.useState(false)
  const [assignError, setAssignError] = React.useState<string | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [specs, assigns, roleRows, activeProfiles, availability] = await Promise.all([
        supportCenterSpecialtiesService.listSpecializations(),
        supportCenterSpecialtiesService.listAssignments(),
        supportCenterSpecialtiesService.listProfessionalRoles(),
        supportCenterSpecialtiesService.listActiveProfiles(),
        supportCenterSpecialtiesService.listAvailability(),
      ])
      setSpecializations(specs)
      setAssignments(assigns)
      setRoles(roleRows)
      setProfiles(activeProfiles)

      const map: Record<string, SupportCenterUserAvailability> = {}
      for (const row of availability) map[row.user_id] = row
      setAvailabilityByUser(map)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const userAssignmentRows = React.useMemo((): UserAssignmentRow[] => {
    const specsByUser = new Map<
      string,
      { specialization_names: string[]; first_delegated_at: string }
    >()

    for (const a of assignments) {
      const existing = specsByUser.get(a.user_id)
      const specName = a.specialization?.name?.trim()
      if (!existing) {
        specsByUser.set(a.user_id, {
          specialization_names: specName ? [specName] : [],
          first_delegated_at: a.created_at,
        })
        continue
      }
      if (specName && !existing.specialization_names.includes(specName)) {
        existing.specialization_names.push(specName)
      }
      if (a.created_at < existing.first_delegated_at) {
        existing.first_delegated_at = a.created_at
      }
    }

    return roles
      .map((roleRow) => {
        const specs = specsByUser.get(roleRow.user_id)
        return {
          user_id: roleRow.user_id,
          user_name: roleRow.profile?.name ?? '—',
          role: roleRow.role,
          specialization_names: [...(specs?.specialization_names ?? [])].sort((a, b) =>
            a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })
          ),
          first_delegated_at: specs?.first_delegated_at ?? roleRow.created_at,
        }
      })
      .sort((a, b) => a.user_name.localeCompare(b.user_name, 'pt-BR', { sensitivity: 'base' }))
  }, [assignments, roles])

  const filteredRows = filter.trim()
    ? userAssignmentRows.filter((row) =>
        row.user_name.toLowerCase().includes(filter.trim().toLowerCase())
      )
    : userAssignmentRows

  const assignableSpecializations = React.useMemo(() => {
    if (!assignUserId) return specializations
    if (assignEditingId) return specializations
    const taken = new Set(
      assignments.filter((a) => a.user_id === assignUserId).map((a) => a.specialization_id)
    )
    return specializations.filter((s) => !taken.has(s.id))
  }, [assignments, assignEditingId, assignUserId, specializations])

  /** Em novo cadastro, só perfis que ainda não têm função. */
  const assignableProfiles = React.useMemo(() => {
    if (assignEditingId) {
      return profiles.filter((p) => p.id === assignEditingId)
    }
    const taken = new Set(roles.map((r) => r.user_id))
    return profiles.filter((p) => !taken.has(p.id))
  }, [assignEditingId, profiles, roles])

  const applyAvailabilityForUser = (userId: string) => {
    const existing = availabilityByUser[userId]
    const map = existing?.hours_by_weekday ?? {}
    const days =
      existing?.weekdays?.length
        ? existing.weekdays
        : Object.keys(map)
            .map(Number)
            .filter((d) => (map[d]?.length ?? 0) > 0)
            .sort((a, b) => a - b)
    setAssignWeekdays(days)
    setAssignHoursByWeekday(map)
  }

  const openAssignModal = () => {
    setAssignError(null)
    setAssignEditingId(null)
    setAssignUserId('')
    setAssignRole('atendente_triagem')
    setAssignSpecializationIds([])
    setAssignWeekdays([])
    setAssignHoursByWeekday({})
    setAssignModalOpen(true)
  }

  const openEditAssignModal = (row: UserAssignmentRow) => {
    setAssignError(null)
    setAssignEditingId(row.user_id)
    setAssignUserId(row.user_id)
    setAssignRole(row.role)
    const userSpecIds = assignments
      .filter((a) => a.user_id === row.user_id)
      .map((a) => a.specialization_id)
    setAssignSpecializationIds([...new Set(userSpecIds)])
    applyAvailabilityForUser(row.user_id)
    setAssignModalOpen(true)
  }

  const closeAssignModal = () => {
    if (submittingAssign) return
    setAssignModalOpen(false)
    setAssignEditingId(null)
    setAssignError(null)
  }

  const handleUserChange = (userId: string) => {
    setAssignUserId(userId)
    setAssignSpecializationIds([])
    applyAvailabilityForUser(userId)
  }

  const handleRoleChange = (role: SupportCenterProfessionalRoleKind) => {
    setAssignRole(role)
    if (role === 'triagem') {
      setAssignSpecializationIds([])
      setAssignWeekdays([])
      setAssignHoursByWeekday({})
    }
  }

  const handleWeekdaysChange = (next: number[]) => {
    setAssignWeekdays(next)
  }

  const handleHoursByWeekdayChange = (next: SupportCenterHoursByWeekday) => {
    setAssignHoursByWeekday(next)
  }

  const toggleAssignSpecialization = (id: string) => {
    setAssignSpecializationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const handleAssignSave = async () => {
    if (!user?.id) return
    if (!assignUserId) {
      setAssignError('Selecione o profissional.')
      return
    }
    if (assignRole === 'atendente_triagem') {
      if (assignSpecializationIds.length === 0) {
        setAssignError('Selecione o profissional e ao menos uma especialidade.')
        return
      }
      if (assignWeekdays.length === 0) {
        setAssignError('Selecione ao menos um dia da semana e uma hora de disponibilidade.')
        return
      }
      const missingHours = assignWeekdays.some(
        (day) => (assignHoursByWeekday[day]?.length ?? 0) === 0
      )
      if (missingHours) {
        setAssignError('Informe ao menos uma hora de disponibilidade para cada dia selecionado.')
        return
      }
    }
    setSubmittingAssign(true)
    setAssignError(null)
    try {
      await supportCenterSpecialtiesService.upsertProfessionalRole({
        user_id: assignUserId,
        role: assignRole,
        actor_id: user.id,
      })

      if (assignRole === 'triagem') {
        await supportCenterSpecialtiesService.softDeleteAllAssignmentsForUser(
          assignUserId,
          'Removido ao definir função Triagem',
          user.id
        )
        await supportCenterSpecialtiesService.softDeleteAvailabilityForUser(
          assignUserId,
          'Removido ao definir função Triagem',
          user.id
        )
      } else {
        const hoursMap: SupportCenterHoursByWeekday = {}
        for (const day of assignWeekdays) {
          hoursMap[day] = assignHoursByWeekday[day] ?? []
        }
        if (assignEditingId) {
          await supportCenterSpecialtiesService.syncUserSpecializations(
            assignUserId,
            assignSpecializationIds,
            user.id
          )
        } else {
          await supportCenterSpecialtiesService.assignSpecializations(
            assignUserId,
            assignSpecializationIds,
            user.id
          )
        }
        await supportCenterSpecialtiesService.upsertAvailability({
          user_id: assignUserId,
          hours_by_weekday: hoursMap,
          actor_id: user.id,
        })
      }

      const profileName = profiles.find((p) => p.id === assignUserId)?.name ?? assignUserId
      const roleLabel = SUPPORT_CENTER_PROFESSIONAL_ROLE_LABELS[assignRole]
      void (assignEditingId
        ? logUpdate(
            'support_center_professional_role',
            assignUserId,
            `Atualizou profissional: ${profileName} (${roleLabel})`,
            undefined,
            {
              role: assignRole,
              specialization_ids:
                assignRole === 'atendente_triagem' ? assignSpecializationIds : [],
            }
          )
        : logCreate(
            'support_center_professional_role',
            assignUserId,
            `Delegou profissional: ${profileName} (${roleLabel})`,
            {
              role: assignRole,
              specialization_ids:
                assignRole === 'atendente_triagem' ? assignSpecializationIds : [],
            }
          ))
      closeAssignModal()
      await loadData()
    } catch (error) {
      setAssignError(getErrorMessage(error))
    } finally {
      setSubmittingAssign(false)
    }
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
    setDeleteReason('')
  }

  const confirmDelete = async () => {
    if (!deleteModal || !user?.id || !deleteReason.trim()) return
    setSubmittingDelete(true)
    try {
      await supportCenterSpecialtiesService.softDeleteAllAssignmentsForUser(
        deleteModal.id,
        deleteReason.trim(),
        user.id
      )
      await supportCenterSpecialtiesService.softDeleteAvailabilityForUser(
        deleteModal.id,
        deleteReason.trim(),
        user.id
      )
      await supportCenterSpecialtiesService.softDeleteProfessionalRoleForUser(
        deleteModal.id,
        deleteReason.trim(),
        user.id
      )
      void logDelete(
        'support_center_professional_role',
        deleteModal.id,
        `Excluiu profissional: ${deleteModal.label}`,
        { reason: deleteReason.trim() }
      )
      closeDeleteModal()
      await loadData()
    } catch (error) {
      setPageError(getErrorMessage(error))
      closeDeleteModal()
    } finally {
      setSubmittingDelete(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      {pageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{pageError}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Profissionais
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              {filteredRows.length}
              {filter.trim() && filteredRows.length !== userAssignmentRows.length
                ? ` de ${userAssignmentRows.length}`
                : ''}{' '}
              {filteredRows.length === 1 ? 'profissional' : 'profissionais'}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrar por profissional..."
              className="w-48 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {canInsert && (
              <Button variant="solid" size="md" onClick={openAssignModal}>
                <PlusIcon className="h-4 w-4" />
                Delegar
              </Button>
            )}
          </div>
        </div>

        {filteredRows.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              {filter.trim()
                ? `Nenhum resultado para "${filter}".`
                : 'Nenhum profissional cadastrado.'}
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Profissional
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Função
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Especialidades
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Agenda
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Delegado em
                  </th>
                  {(canUpdate || canDelete) && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredRows.map((row) => {
                  const avail = availabilityByUser[row.user_id]
                  const isTriageOnly = row.role === 'triagem'
                  const specsLabel =
                    row.specialization_names.length > 0
                      ? row.specialization_names.join(', ')
                      : '—'
                  return (
                    <tr
                      key={row.user_id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-3 align-top text-sm font-medium text-gray-900 dark:text-gray-100">
                        {row.user_name}
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-gray-500 dark:text-gray-400">
                        {SUPPORT_CENTER_PROFESSIONAL_ROLE_LABELS[row.role]}
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-gray-500 dark:text-gray-400">
                        {isTriageOnly ? '—' : specsLabel}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {isTriageOnly ? (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        ) : (
                          <AvailabilitySchedule
                            hoursByWeekday={avail?.hours_by_weekday ?? {}}
                            weekdays={avail?.weekdays ?? []}
                            hours={avail?.hours ?? []}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(row.first_delegated_at)}
                      </td>
                      {(canUpdate || canDelete) && (
                        <td className="px-4 py-3 align-top text-center">
                          <div className="flex items-center justify-center gap-2">
                            {canUpdate && (
                              <button
                                type="button"
                                title="Editar"
                                onClick={() => openEditAssignModal(row)}
                                className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                title="Remover profissional"
                                onClick={() => {
                                  setDeleteModal({
                                    id: row.user_id,
                                    label: `${row.user_name} (${SUPPORT_CENTER_PROFESSIONAL_ROLE_LABELS[row.role]})`,
                                  })
                                  setDeleteReason('')
                                }}
                                className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {assignModalOpen && (
        <ParametrosAssignModal
          assignEditingId={assignEditingId}
          assignUserId={assignUserId}
          assignRole={assignRole}
          assignSpecializationIds={assignSpecializationIds}
          assignWeekdays={assignWeekdays}
          assignHoursByWeekday={assignHoursByWeekday}
          assignError={assignError}
          submitting={submittingAssign}
          profiles={assignableProfiles}
          assignableSpecializations={assignableSpecializations}
          onUserChange={handleUserChange}
          onRoleChange={handleRoleChange}
          onToggleSpecialization={toggleAssignSpecialization}
          onWeekdaysChange={handleWeekdaysChange}
          onHoursByWeekdayChange={handleHoursByWeekdayChange}
          onClose={closeAssignModal}
          onSave={() => void handleAssignSave()}
        />
      )}

      {deleteModal && (
        <ParametrosDeleteModal
          label={deleteModal.label}
          deleteReason={deleteReason}
          submitting={submittingDelete}
          onReasonChange={setDeleteReason}
          onClose={closeDeleteModal}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  )
}

export default ParametrosUsuariosTab
